import React, { useState, useEffect, useRef, useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { NavLink } from 'react-router-dom';
import doesNodeContainClick from '@kitconcept/volto-light-theme/helpers/doesNodeContainClick';
import { useIntl, defineMessages } from 'react-intl';
import cx from 'classnames';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';
import config from '@plone/volto/registry';

import { getNavigation } from '@plone/volto/actions/navigation/navigation';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import clearSVG from '@plone/volto/icons/clear.svg';
import NavItem from '@plone/volto/components/theme/Navigation/NavItem';

const messages = defineMessages({
  closeMenu: {
    id: 'Close menu',
    defaultMessage: 'Close menu',
  },
  openFatMenu: {
    id: 'Open menu',
    defaultMessage: 'Open menu',
  },
  moreMenu: {
    id: 'moreMenu',
    defaultMessage: 'Mais',
  },
});

type NavigationItem = {
  title: string;
  nav_title?: string;
  url: string;
  items?: NavigationItem[];
};

type NavigationProps = {
  pathname: string;
};

type HeaderSettings = {
  has_fat_menu?: boolean;
};

type RootState = {
  content: {
    data?: {
      '@components'?: {
        inherit?: {
          'voltolighttheme.header'?: {
            data?: HeaderSettings;
          };
        };
      };
    };
  };
  form: {
    global?: {
      has_fat_menu?: boolean;
      [key: string]: unknown;
    };
  };
  intl: {
    locale: string;
  };
  userSession: {
    token?: string | null;
  };
  navigation: {
    items: NavigationItem[];
  };
};

const Navigation = ({ pathname }: NavigationProps) => {
  const [desktopMenuOpen, setDesktopMenuOpen] = useState<number | null>(null);
  const currentOpenIndex = useRef<number | null>(null);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const navigation = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);
  const moreRef = useRef<HTMLLIElement | null>(null);
  const dispatch = useDispatch();
  const intl = useIntl();
  const headerSettings = useSelector(
    (state: RootState) =>
      state.content.data?.['@components']?.inherit?.['voltolighttheme.header']
        ?.data,
  );
  const formData = useSelector((state: RootState) => state.form.global);

  const hasFatMenuSetting =
    !isEmpty(formData) && formData?.has_fat_menu !== undefined
      ? formData.has_fat_menu
      : headerSettings?.has_fat_menu;
  const hasFatMenu = hasFatMenuSetting ?? false;

  const lang = useSelector((state: RootState) => state.intl.locale);
  const token = useSelector(
    (state: RootState) => state.userSession.token,
    shallowEqual,
  );
  const items = useSelector(
    (state: RootState) => state.navigation.items,
    shallowEqual,
  );

  const closeMenu = () => {
    setDesktopMenuOpen(null);
    currentOpenIndex.current = null;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navigation.current && doesNodeContainClick(navigation.current, event))
        return;
      closeMenu();
      setMoreMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside, false);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  }, []);

  useEffect(() => {
    if (!hasApiExpander('navigation', getBaseUrl(pathname))) {
      dispatch(getNavigation(getBaseUrl(pathname), config.settings.navDepth));
    }
  }, [pathname, token, dispatch]);

  const isActive = (url: string) => {
    return (url === '' && pathname === '/') || (url !== '' && pathname === url);
  };

  const openMenu = (index: number) => {
    if (index === currentOpenIndex.current) {
      setDesktopMenuOpen(null);
      currentOpenIndex.current = null;
    } else {
      setDesktopMenuOpen(index);
      currentOpenIndex.current = index;
    }
  };

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.keyCode === 27) {
        closeMenu();
        setMoreMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const calculateVisibleItems = useCallback(() => {
    const nav = navigation.current;
    const menu = menuRef.current;
    if (!nav || !menu || items.length === 0) return;

    const children = Array.from(menu.children) as HTMLElement[];
    const availableWidth = nav.clientWidth;
    let moreButtonWidth = 100;

    let totalWidth = 0;
    let count = 0;

    for (let i = 0; i < children.length; i++) {
      const child = children[i];

      if (child.classList.contains('more-menu-wrapper')) {
        moreButtonWidth = child.getBoundingClientRect().width;
        continue;
      }

      const childWidth = child.getBoundingClientRect().width;
      const style = window.getComputedStyle(child);
      const marginLeft = parseFloat(style.marginLeft) || 0;
      const marginRight = parseFloat(style.marginRight) || 0;
      const fullWidth = childWidth + marginLeft + marginRight;

      if (totalWidth + fullWidth + moreButtonWidth <= availableWidth) {
        totalWidth += fullWidth;
        count++;
      } else {
        break;
      }
    }

    const newCount = count >= items.length ? items.length : Math.max(count, 1);
    setVisibleCount(newCount);
  }, [items]);

  useEffect(() => {
    calculateVisibleItems();

    const observer = new ResizeObserver(() => {
      calculateVisibleItems();
    });

    if (navigation.current) {
      observer.observe(navigation.current);
    }

    return () => observer.disconnect();
  }, [calculateVisibleItems]);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const visibleItems =
    visibleCount !== null ? items.slice(0, visibleCount) : items;
  const overflowItems = visibleCount !== null ? items.slice(visibleCount) : [];

  const renderMenuItem = (item: NavigationItem, index: number) => (
    <li key={item.url}>
      {hasFatMenu ? (
        <>
          <button
            type="button"
            onClick={() => openMenu(index)}
            className={cx('item', {
              active:
                desktopMenuOpen === index ||
                (!desktopMenuOpen && pathname === item.url),
            })}
            aria-label={intl.formatMessage(messages.openFatMenu)}
            aria-expanded={desktopMenuOpen === index}
          >
            {item.title}
          </button>

          <div className="submenu-wrapper">
            <div
              className={cx('submenu', {
                active: desktopMenuOpen === index,
              })}
            >
              <div className="submenu-inner">
                <NavLink
                  to={item.url === '' ? '/' : item.url}
                  onClick={() => closeMenu()}
                  className="submenu-header"
                >
                  <h2>{item.nav_title ?? item.title}</h2>
                </NavLink>
                <button
                  type="button"
                  className="close"
                  onClick={closeMenu}
                  aria-label={intl.formatMessage(messages.closeMenu)}
                >
                  <Icon name={clearSVG} size="48px" />
                </button>
                <ul>
                  {item.items &&
                    item.items.length > 0 &&
                    item.items.map((subitem) => (
                      <li className="subitem-wrapper" key={subitem.url}>
                        <NavLink
                          to={subitem.url}
                          onClick={() => closeMenu()}
                          className={cx({
                            current: isActive(subitem.url),
                          })}
                        >
                          <span className="left-arrow">&#8212;</span>
                          <span>{subitem.nav_title || subitem.title}</span>
                        </NavLink>
                        <div className="sub-submenu">
                          <ul>
                            {subitem.items &&
                              subitem.items.length > 0 &&
                              subitem.items.map((subsubitem) => (
                                <li
                                  className="subsubitem-wrapper"
                                  key={subsubitem.url}
                                >
                                  <NavLink
                                    to={subsubitem.url}
                                    onClick={() => closeMenu()}
                                    className={cx({
                                      current: isActive(subsubitem.url),
                                    })}
                                  >
                                    <span className="left-arrow">&#8212;</span>
                                    <span>
                                      {subsubitem.nav_title || subsubitem.title}
                                    </span>
                                  </NavLink>
                                </li>
                              ))}
                          </ul>
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        </>
      ) : (
        <NavItem item={item} lang={lang} key={item.url} />
      )}
    </li>
  );

  return (
    <nav
      id="navigation"
      aria-label="navigation"
      className="navigation"
      ref={navigation}
    >
      <div className={'computer large screen widescreen only'}>
        {/* Invisible measurement menu */}
        <ul
          className="desktop-menu"
          ref={menuRef}
          aria-hidden="true"
          style={{
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
            top: 0,
            left: 0,
            width: '100%',
            zIndex: -1,
          }}
        >
          {items.map((item) => (
            <li key={`measure-${item.url}`}>
              {hasFatMenu ? (
                <button type="button" className="item" tabIndex={-1}>
                  {item.title}
                </button>
              ) : (
                <NavLink
                  to={item.url === '' ? '/' : item.url}
                  className="item"
                  tabIndex={-1}
                >
                  {item.nav_title || item.title}
                </NavLink>
              )}
            </li>
          ))}
          <li className="more-menu-wrapper">
            <button
              type="button"
              className="item more-menu-trigger"
              tabIndex={-1}
            >
              {intl.formatMessage(messages.moreMenu)} ▾
            </button>
          </li>
        </ul>

        {/* Visible menu */}
        <ul className="desktop-menu">
          {visibleItems.map((item, index) => renderMenuItem(item, index))}

          {overflowItems.length > 0 && (
            <li className="more-menu-wrapper" ref={moreRef}>
              <button
                type="button"
                className={cx('item more-menu-trigger', {
                  active: moreMenuOpen,
                })}
                onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                aria-expanded={moreMenuOpen}
                aria-label={intl.formatMessage(messages.moreMenu)}
              >
                {intl.formatMessage(messages.moreMenu)} ▾
              </button>

              <div className="submenu-wrapper">
                <div className={cx('submenu', { active: moreMenuOpen })}>
                  <div className="submenu-inner">
                    <div className="submenu-header">
                      <h2>{intl.formatMessage(messages.moreMenu)}</h2>
                    </div>
                    <button
                      type="button"
                      className="close"
                      onClick={() => setMoreMenuOpen(false)}
                      aria-label={intl.formatMessage(messages.closeMenu)}
                    >
                      <Icon name={clearSVG} size="48px" />
                    </button>
                    <ul>
                      {overflowItems.map((item, i) => (
                        <li className="subitem-wrapper" key={item.url}>
                          <NavLink
                            to={item.url === '' ? '/' : item.url}
                            onClick={() => setMoreMenuOpen(false)}
                            className={cx({ current: isActive(item.url) })}
                          >
                            <span className="left-arrow">&#8212;</span>
                            <span>{item.nav_title || item.title}</span>
                          </NavLink>
                          {item.items && item.items.length > 0 && (
                            <div className="sub-submenu">
                              <ul>
                                {item.items.map((subitem) => (
                                  <li
                                    className="subsubitem-wrapper"
                                    key={subitem.url}
                                  >
                                    <NavLink
                                      to={subitem.url}
                                      onClick={() => setMoreMenuOpen(false)}
                                      className={cx({
                                        current: isActive(subitem.url),
                                      })}
                                    >
                                      <span className="left-arrow">
                                        &#8212;
                                      </span>
                                      <span>
                                        {subitem.nav_title || subitem.title}
                                      </span>
                                    </NavLink>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </li>
          )}
        </ul>
      </div>
    </nav>
  );
};

export default Navigation;
